// src/pages/CalendarPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Box,
  Tooltip,
  Badge,
  Modal,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  AlertTitle,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DateTimePicker, LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Close as CloseIcon } from '@mui/icons-material';
import { FaGlobe, FaFacebookF, FaWhatsapp, FaInstagram, FaPinterestP, FaBars, FaHome, FaBook } from 'react-icons/fa';

import { RRule } from 'rrule';
import {
  collection,
  addDoc,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

import { db, auth } from '../firebase';
import Sidebar from '../components/sidebar/Sidebar';
import { useNavigate } from 'react-router-dom';

import '../styles/CalendarPage.css';

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const SUPERUSERS = new Set(['davidedessi@me.com', 'sabanici4@gmail.com']);

const CATEGORIES = [
  { name: 'Parrocchia', color: '#2196F3', text: '#fff' },
  { name: 'Catechismo', color: '#4CAF50', text: '#fff' },
  { name: 'Eventi', color: '#FF9800', text: '#fff' },
  { name: 'Altro', color: '#9C27B0', text: '#fff' },
  { name: 'Compleanno', color: '#FFEB3B', text: '#000' },
];

// --- Utils date ---
function parseBirthDate(bd) {
  if (!bd) return null;
  try {
    if (bd?.seconds && typeof bd.seconds === 'number') return moment(bd.toDate());
    if (bd instanceof Date) return moment(bd);
    return moment(bd);
  } catch {
    return null;
  }
}

function computeBirthdayOccurrence(birthMoment, forwardIfPast) {
  if (!birthMoment || !birthMoment.isValid()) return null;
  const now = moment();
  let start = moment({ year: now.year(), month: birthMoment.month(), day: birthMoment.date() }).startOf('day');
  let end = moment(start).endOf('day');
  if (forwardIfPast && start.isBefore(now, 'day')) {
    start = start.add(1, 'year');
    end = end.add(1, 'year');
  }
  return { start, end };
}

// Event content (thumb + titolo) con hover/tap preview stabile
function makeEventContent(openPreview, closePreview, previewForEventRef) {
  return function EventContent({ event }) {
    const onEnter = (e) => {
if (event.coverUrl) openPreview(event.coverUrl, e.currentTarget, event.id);
    };
    const onLeave = () => {
if (previewForEventRef.current === event.id) closePreview();
    };
    const onTouch = (e) => {
      if (!event.coverUrl) return;
      e.preventDefault(); e.stopPropagation();
openPreview(event.coverUrl, e.currentTarget, event.id);
    };
    const onTouchEnd = () => {
if (previewForEventRef.current === event.id) closePreview();
    };
    return (
      <div
        className="rbc-event-inner"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onTouchStart={onTouch}
        onTouchEnd={onTouchEnd}
        style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 2 }}
      >
        {event.coverUrl && (
          <span
            aria-hidden="true"
            style={{
              flex: '0 0 18px',
              width: 18,
              height: 18,
              borderRadius: 3,
              backgroundImage: `url("${event.coverUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '0 0 0 1px rgba(255,255,255,0.65) inset',
            }}
          />
        )}
        <span
          style={{
            display: 'inline-block',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {event.title}
        </span>
      </div>
    );
  };
}

function CalendarPage() {
  // === STATE base ===
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // campi evento
  const [eventTitle, setEventTitle] = useState('');
  const [eventStart, setEventStart] = useState(null);
  const [eventEnd, setEventEnd] = useState(null);
  const [recurrence, setRecurrence] = useState('none');
  const [category, setCategory] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [coverUrl, setCoverUrl] = useState('');
  const [studentId, setStudentId] = useState('');

  // compleanni: sposta al prossimo se passato
  const [shiftPastBirthdayToNext, setShiftPastBirthdayToNext] = useState(true);

  // filtri
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // ui
  const [isOpenSidebar, setIsOpenSidebar] = useState(false);
  const [snack, setSnack] = useState({ open: false, type: 'info', msg: '' });
  const [uploadingName, setUploadingName] = useState('');
  const [preloadingPhotos, setPreloadingPhotos] = useState(false);

  // badge
  const [upcomingEventsCount, setUpcomingEventsCount] = useState(0);
  const [birthdayNotifications, setBirthdayNotifications] = useState(0);

  // hover/preview gestione cover
  const [hoveredId, setHoveredId] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: '', anchorEl: null });
  const previewForEventRef = useRef(null);
  const openPreview = useCallback((url, anchorEl, eventId) => {
    setPreview({ open: true, url, anchorEl });
    previewForEventRef.current = eventId;
  }, []);
  const closePreview = useCallback(() => {
    setPreview((p) => ({ ...p, open: false }));
    previewForEventRef.current = null;
  }, []);

  // multi-export .ics
  const [selectedForExport, setSelectedForExport] = useState(() => new Set());
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  // studenti (per compleanni)
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // auth/nav
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [canPublishToAll, setCanPublishToAll] = useState(false);
  const [publishToAll, setPublishToAll] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [pendingPublishValue, setPendingPublishValue] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const navigate = useNavigate();

  moment.locale('it');

  const [view, setView] = useState(() => {
    const v = localStorage.getItem('calendarView');
    return v && Object.values(Views).includes(v) ? v : Views.MONTH;
  });
  const [date, setDate] = useState(new Date());

  const [showHint, setShowHint] = useState(() => localStorage.getItem('calendarHintDismissed') !== '1');
  const [showWelcomeSnack, setShowWelcomeSnack] = useState(() => localStorage.getItem('calendarSnackShown') !== '1');

  const searchRef = useRef(searchQuery);
  useEffect(() => { searchRef.current = searchQuery; }, [searchQuery]);

  const storage = getStorage();
  async function uploadCoverFile(file) {
    if (!file || !userId) return null;
    try {
      setUploadingCover(true);
      const name = file.name || 'cover.jpg';
      const ext = name.includes('.') ? name.split('.').pop() : 'jpg';
      const path = `event-covers/${userId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file);
      const url = await getDownloadURL(sref);
      setCoverUrl(url);
      notify('success', 'Copertina caricata.');
      return url;
    } catch (e) {
      console.error(e);
      notify('error', 'Errore nel caricamento della copertina.');
      return null;
    } finally {
      setUploadingCover(false);
    }
  }

  // === Helpers ricorrenza ===
  const getRRuleString = useCallback((rec) => {
    switch (rec) {
      case 'daily': return 'FREQ=DAILY';
      case 'weekly': return 'FREQ=WEEKLY';
      case 'monthly': return 'FREQ=MONTHLY';
      case 'yearly': return 'FREQ=YEARLY';
      default: return null;
    }
  }, []);

  const getRecurrenceFromRRule = useCallback((recurrenceStr) => {
    if (!recurrenceStr || recurrenceStr === 'none') return 'none';
    const m = recurrenceStr.match(/FREQ=(\w+);?/);
    if (!m) return 'none';
    const f = m[1];
    return f === 'DAILY' ? 'daily' : f === 'WEEKLY' ? 'weekly' : f === 'MONTHLY' ? 'monthly' : f === 'YEARLY' ? 'yearly' : 'none';
  }, []);

  // === Auth + eventi (miei + pubblici) ===
  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (!user) { navigate('/login'); return; }
      setUserId(user.uid);
      const em = (user.email || '').toLowerCase();
      setUserEmail(em);
      setCanPublishToAll(SUPERUSERS.has(em));

      // Eventi miei
      const qMine = query(collection(db, 'events'), where('userId', '==', user.uid));
      const unsubMine = onSnapshot(qMine, async (snap) => {
        const mine = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title,
            start: data.start?.toDate ? data.start.toDate() : data.start,
            end: data.end?.toDate ? data.end.toDate() : data.end,
            recurrence: data.recurrence || 'none',
            category: data.category || '',
            allDay: !!data.allDay,
            coverUrl: data.coverUrl || '',
            studentId: data.studentId || '',
            userId: data.userId,
            visibility: data.visibility || 'self',
          };
        });
        setEvents((prev) => {
          const publics = Array.isArray(prev) ? prev.filter((ev) => ev.visibility === 'all' && ev.userId !== user.uid) : [];
          return [...mine, ...publics];
        });

        // Autocopertina per compleanni mancanti
        const missing = mine.filter((e) => e.category === 'Compleanno' && !e.coverUrl);
        if (missing.length) {
          setPreloadingPhotos(true);
          try {
            for (const ev of missing) {
              // eslint-disable-next-line no-await-in-loop
              const p = await fetchStudentPhotoByName(ev.title);
              if (p) {
                // eslint-disable-next-line no-await-in-loop
                await updateDoc(doc(db, 'events', ev.id), { coverUrl: p });
              }
            }
          } catch (err) {
            console.error('Preload covers error:', err);
          } finally {
            setPreloadingPhotos(false);
          }
        }
      });

      // Eventi pubblici
      const qPublic = query(collection(db, 'events'), where('visibility', '==', 'all'));
      const unsubPublic = onSnapshot(qPublic, (snap) => {
        const pubs = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title,
            start: data.start?.toDate ? data.start.toDate() : data.start,
            end: data.end?.toDate ? data.end.toDate() : data.end,
            recurrence: data.recurrence || 'none',
            category: data.category || '',
            allDay: !!data.allDay,
            coverUrl: data.coverUrl || '',
            studentId: data.studentId || '',
            userId: data.userId,
            visibility: 'all',
          };
        });
        setEvents((prev) => {
          const mine = Array.isArray(prev) ? prev.filter((ev) => ev.userId === user.uid || ev.visibility !== 'all') : [];
          return [...mine, ...pubs];
        });
      });

      return () => { unsubMine(); unsubPublic(); };
    });
    return () => unsubAuth();
  }, [navigate]);

  // === Badge (entro 12 mesi) ===
  useEffect(() => {
    if (!events.length) { setUpcomingEventsCount(0); setBirthdayNotifications(0); return; }
    const now = moment().startOf('day');
    const until = moment().add(1, 'year').endOf('day');
    let up = 0; let bd = 0;

    events.forEach((e) => {
      let occs = [];
      if (e.recurrence && e.recurrence !== 'none') {
        try {
          const opt = { ...RRule.parseString(e.recurrence), dtstart: e.start, until: until.toDate() };
          if (e.category === 'Compleanno') {
            opt.freq = RRule.YEARLY;
            opt.bymonth = moment(e.start).month() + 1;
            opt.bymonthday = moment(e.start).date();
            opt.dtstart = now.toDate();
          }
          const r = new RRule(opt);
          occs = r.between(now.toDate(), until.toDate(), true);
        } catch (err) { /* ignore */ }
      } else if (moment(e.start).isSameOrAfter(now) && moment(e.start).isSameOrBefore(until)) {
        occs.push(e.start);
      }
      if (occs.length) {
        if (e.category === 'Compleanno') bd += occs.length;
        else up += occs.length;
      }
    });

    setUpcomingEventsCount(up);
    setBirthdayNotifications(bd);
  }, [events]);

  // === Lista prossimi appuntamenti ===
  const getUpcomingEvents = useCallback(() => {
    const now = moment().startOf('day');
    const until = moment().add(1, 'year').endOf('day');
    const res = [];

    events.forEach((e) => {
      if (e.recurrence && e.recurrence !== 'none') {
        try {
          const opt = { ...RRule.parseString(e.recurrence), dtstart: e.start, until: until.toDate() };
          if (e.category === 'Compleanno') {
            opt.freq = RRule.YEARLY;
            opt.bymonth = moment(e.start).month() + 1;
            opt.bymonthday = moment(e.start).date();
            opt.dtstart = now.toDate();
          }
          const r = new RRule(opt);
          const occs = r.between(now.toDate(), until.toDate(), true);
          occs.forEach((o) => {
            const s = new Date(o);
            const d = Math.max((e.end - e.start) || 0, 30 * 60 * 1000);
            res.push({ ...e, start: s, end: new Date(s.getTime() + d) });
          });
        } catch (err) { /* ignore */ }
      } else if (moment(e.start).isSameOrAfter(now) && moment(e.start).isSameOrBefore(until)) {
        res.push(e);
      }
    });

    return res.sort((a, b) => a.start - b.start);
  }, [events]);

  // === Stile evento (cover + hover) ===
  const eventStyleGetter = useCallback((event) => {
    const cat = CATEGORIES.find((c) => c.name === event.category);
    const style = {
      borderRadius: 6,
      border: 0,
      opacity: 1,
      color: cat ? cat.text : '#fff',
      backgroundColor: cat ? cat.color : '#3174ad',
      overflow: 'hidden',
      minHeight: 22,
    };
    return { style };
  }, []);
// === i18n ===
  const messages = useMemo(() => ({
    next: 'Successivo',
    previous: 'Precedente',
    today: 'Oggi',
    month: 'Mese',
    week: 'Settimana',
    day: 'Giorno',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Ora',
    event: 'Evento',
    allDay: 'Tutto il giorno',
    noEventsInRange: 'Nessun evento in questo intervallo.',
    showMore: (t) => `+ Altri (${t})`,
  }), []);

  const formats = useMemo(() => ({
    dayFormat: (d) => moment(d).format('dd DD/MM'),
    dayHeaderFormat: (d) => moment(d).format('dddd D MMMM YYYY'),
    dayRangeHeaderFormat: ({ start, end }) => `${moment(start).format('D MMM')} – ${moment(end).format('D MMM YYYY')}`,
    agendaHeaderFormat: ({ start, end }) => `${moment(start).format('D MMM')} – ${moment(end).format('D MMM YYYY')}`,
    agendaDateFormat: (d) => moment(d).format('dd DD/MM'),
    agendaTimeFormat: (d) => moment(d).format('HH:mm'),
  }), []);

  const currentDateFormatted = useMemo(() => moment().format('dddd, D MMMM YYYY'), []);

  // === snack ===
  const notify = useCallback((type, msg) => setSnack({ open: true, type, msg }), []);
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  useEffect(() => {
    if (showWelcomeSnack) {
      setSnack({ open: true, type: 'info', msg: 'Suggerimento: clicca su un giorno del calendario per aggiungere un appuntamento.' });
      localStorage.setItem('calendarSnackShown', '1');
    }
  }, [showWelcomeSnack]);

  // === foto studente ===
  const fetchStudentPhotoByName = useCallback(async (fullName) => {
    if (!fullName) return '';
    try {
      // prima prova il match su fullName
      let qStu = query(collection(db, 'students'), where('fullName', '==', fullName));
      let snap = await getDocs(qStu);
      if (!snap.empty) {
        const data = snap.docs[0].data();
        return (
          data.foto || data.immagine ||
          data.fotoURL || data.fotoUrl ||
          data.photoURL || data.photoUrl ||
          data.avatar || ''
        );
      }
      // fallback: prova a dividere nome/cognome
      const parts = fullName.split(' ').filter(Boolean);
      if (parts.length >= 2) {
        const nome = parts.slice(0, -1).join(' ');
        const cognome = parts.slice(-1).join('');
        qStu = query(collection(db, 'students'), where('nome', '==', nome), where('cognome', '==', cognome));
        snap = await getDocs(qStu);
        if (!snap.empty) {
          const data = snap.docs[0].data();
          return (
            data.foto || data.immagine ||
            data.fotoURL || data.fotoUrl ||
            data.photoURL || data.photoUrl ||
            data.avatar || ''
          );
        }
      }
    } catch (err) {
      console.error('fetchStudentPhotoByName error:', err);
    }
    return '';
  }, []);

  const uploadStudentPhoto = useCallback(async (studentFullName, file) => {
    if (!file || !studentFullName) return null;
    try {
      setUploadingName(studentFullName);

      // trova la scheda per fullName oppure (nome,cognome)
      let sdoc = null;
      let qStu = query(collection(db, 'students'), where('fullName', '==', studentFullName));
      let snap = await getDocs(qStu);
      if (!snap.empty) sdoc = snap.docs[0];
      if (!sdoc) {
        const parts = studentFullName.split(' ').filter(Boolean);
        if (parts.length >= 2) {
          const nome = parts.slice(0, -1).join(' ');
          const cognome = parts.slice(-1).join('');
          qStu = query(collection(db, 'students'), where('nome', '==', nome), where('cognome', '==', cognome));
          snap = await getDocs(qStu);
          if (!snap.empty) sdoc = snap.docs[0];
        }
      }
      if (!sdoc) {
        notify('error', 'Scheda studente non trovata.');
        setUploadingName('');
        return null;
      }
      const sid = sdoc.id;

      const path = `students/${sid}/${Date.now()}_${file.name}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file);
      const url = await getDownloadURL(sref);

      await updateDoc(doc(db, 'students', sid), { foto: url, photoURL: url });

      const toUpd = events.filter((e) => e.title === studentFullName && e.category === 'Compleanno');
      for (const ev of toUpd) {
        // eslint-disable-next-line no-await-in-loop
        await updateDoc(doc(db, 'events', ev.id), { coverUrl: url });
      }

      notify('success', 'Foto caricata e collegata allo studente.');
      return url;
    } catch (err) {
      console.error('uploadStudentPhoto error:', err);
      notify('error', 'Errore nel caricamento della foto.');
      return null;
    } finally {
      setUploadingName('');
    }
  }, [events, notify, storage]);

  // === Caricamento studenti on-demand per "Compleanno" ===
  const loadStudentsIfNeeded = useCallback(async () => {
    if (loadingStudents || students.length > 0) return;
    try {
      setLoadingStudents(true);
      const snap = await getDocs(collection(db, 'students'));
      const arr = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          fullName: data.fullName || `${data.nome || data.name || ''} ${data.cognome || data.surname || ''}`.trim(),
          birthDate: data.dataNascita || data.data_di_nascita || data.birthDate || data.birthday || data.dateOfBirth || null,
          photoURL: data.foto || data.immagine || data.fotoURL || data.fotoUrl || data.photoURL || data.photoUrl || data.avatar || '',
        };
      }).filter((s) => s.fullName);
      setStudents(arr);
    } catch (err) {
      console.error('loadStudentsIfNeeded error:', err);
      notify('error', 'Impossibile caricare l’elenco studenti.');
    } finally {
      setLoadingStudents(false);
    }
  }, [loadingStudents, notify, students.length]);

  useEffect(() => {
    if (modalOpen && category === 'Compleanno') loadStudentsIfNeeded();
  }, [modalOpen, category, loadStudentsIfNeeded]);

  useEffect(() => {
    if (editModalOpen && category === 'Compleanno') loadStudentsIfNeeded();
  }, [editModalOpen, category, loadStudentsIfNeeded]);

  // === slots & events ===
  const handleSelectSlot = useCallback((slot) => {
    setEventTitle('');
    setEventStart(moment(slot.start));
    setEventEnd(moment(slot.end));
    setRecurrence('none');
    setCategory('');
    setAllDay(false);
    setCoverUrl('');
    setStudentId('');
    setShiftPastBirthdayToNext(true);
    setPublishToAll(false);
    setModalOpen(true);
  }, []);

  const handleSelectEvent = useCallback((eventObj) => { closePreview();
    setSelectedEvent(eventObj);
    setEventTitle(eventObj.title);
    setEventStart(moment(eventObj.start));
    setEventEnd(moment(eventObj.end));
    setRecurrence(getRecurrenceFromRRule(eventObj.recurrence));
    setCategory(eventObj.category || '');
    setAllDay(!!eventObj.allDay);
    setCoverUrl(eventObj.coverUrl || '');
    setStudentId(eventObj.studentId || '');
    setShiftPastBirthdayToNext(true);
    setPublishToAll(eventObj.visibility === 'all');
    setEditModalOpen(true);
  }, [getRecurrenceFromRRule]);

  // === CRUD ===
  const ensureBirthdayPositioned = useCallback((startMoment, forwardIfPast) => {
    if (!startMoment) return { start: null, end: null };
    const birth = moment(startMoment);
    const occ = computeBirthdayOccurrence(birth, forwardIfPast);
    return occ || { start: startMoment, end: moment(startMoment).endOf('day') };
  }, []);

  const handleAddEvent = useCallback(async () => {
    if (!(eventTitle && eventStart && (allDay || eventEnd))) return notify('warning', 'Compila tutti i campi obbligatori.');
    if (!allDay && eventEnd.isBefore(eventStart)) return notify('warning', 'La data/ora di fine non può precedere l’inizio.');
    if (!userId) return null;

    const recurrenceStr = getRRuleString(recurrence);
    let finalCover = (coverUrl || '').trim();
    let finalStudentId = (studentId || '').trim();

    let startToSave = eventStart.clone();
    let endToSave = (allDay ? moment(eventStart).endOf('day') : eventEnd).clone();

    if (category === 'Compleanno') {
      const occ = ensureBirthdayPositioned(eventStart, shiftPastBirthdayToNext);
      if (occ?.start && occ?.end) {
        startToSave = occ.start.clone();
        endToSave = occ.end.clone();
      }
      if (!finalCover) {
        const photo = await fetchStudentPhotoByName(eventTitle.trim());
        if (photo) finalCover = photo;
      }
    }

    try {
      await addDoc(collection(db, 'events'), {
        userId,
        title: eventTitle.trim(),
        start: startToSave.toDate(),
        end: endToSave.toDate(),
        recurrence: recurrenceStr || 'none',
        category: category || '',
        allDay: !!allDay || category === 'Compleanno',
        coverUrl: finalCover,
        visibility: canPublishToAll && publishToAll ? 'all' : 'self',
        publishedByEmail: userEmail || '',
        studentId: finalStudentId,
      });
      setModalOpen(false);
      setEventTitle(''); setEventStart(null); setEventEnd(null);
      setRecurrence('none'); setCategory(''); setAllDay(false);
      setCoverUrl(''); setStudentId('');
      setShiftPastBirthdayToNext(true);
      notify('success', 'Evento aggiunto con successo.');
      if (canPublishToAll && publishToAll) {
        try {
          await addDoc(collection(db, 'notifications'), {
            type: 'event_added',
            title: eventTitle.trim(),
            category: category || '',
            when: startToSave.toDate(),
            createdBy: userEmail || '',
            visibility: 'all',
            createdAt: new Date(),
          });
        } catch (e) { console.error('notify add err', e); }
      }
      return null;
    } catch (err) {
      console.error('Errore aggiunta evento:', err);
      notify('error', "Errore durante l'aggiunta dell'evento.");
      return null;
    }
  }, [allDay, category, coverUrl, ensureBirthdayPositioned, eventEnd, eventStart, eventTitle, fetchStudentPhotoByName, getRRuleString, notify, recurrence, shiftPastBirthdayToNext, studentId, userId, canPublishToAll, publishToAll, userEmail]);

  const handleEditEvent = useCallback(async () => {
    if (!(eventTitle && eventStart && (allDay || eventEnd) && selectedEvent)) return notify('warning', 'Compila tutti i campi obbligatori.');
    if (!allDay && eventEnd.isBefore(eventStart)) return notify('warning', 'La data/ora di fine non può precedere l’inizio.');
    const recurrenceStr = getRRuleString(recurrence);
    let finalCover = (coverUrl || '').trim();
    let finalStudentId = (studentId || '').trim();

    let startToSave = eventStart.clone();
    let endToSave = (allDay ? moment(eventStart).endOf('day') : eventEnd).clone();

    if (category === 'Compleanno') {
      const occ = ensureBirthdayPositioned(eventStart, shiftPastBirthdayToNext);
      if (occ?.start && occ?.end) {
        startToSave = occ.start.clone();
        endToSave = occ.end.clone();
      }
      if (!finalCover) {
        const photo = await fetchStudentPhotoByName(eventTitle.trim());
        if (photo) finalCover = photo;
      }
    }

    try {
      await updateDoc(doc(db, 'events', selectedEvent.id), {
        title: eventTitle.trim(),
        start: startToSave.toDate(),
        end: endToSave.toDate(),
        recurrence: recurrenceStr || 'none',
        category: category || '',
        allDay: !!allDay || category === 'Compleanno',
        coverUrl: finalCover,
        visibility: canPublishToAll && publishToAll ? 'all' : 'self',
        publishedByEmail: userEmail || '',
        studentId: finalStudentId,
      });
      setEditModalOpen(false);
      setSelectedEvent(null);
      setEventTitle(''); setEventStart(null); setEventEnd(null);
      setRecurrence('none'); setCategory(''); setAllDay(false);
      setCoverUrl(''); setStudentId('');
      setShiftPastBirthdayToNext(true);
      notify('success', 'Evento aggiornato con successo.');
      if (canPublishToAll && publishToAll) {
        try {
          await addDoc(collection(db, 'notifications'), {
            type: 'event_updated',
            title: eventTitle.trim(),
            category: category || '',
            when: startToSave.toDate(),
            createdBy: userEmail || '',
            visibility: 'all',
            createdAt: new Date(),
          });
        } catch (e) { console.error('notify upd err', e); }
      }
      return null;
    } catch (err) {
      console.error('Errore modifica evento:', err);
      notify('error', "Errore durante la modifica dell'evento.");
      return null;
    }
  }, [allDay, category, coverUrl, ensureBirthdayPositioned, eventEnd, eventStart, eventTitle, fetchStudentPhotoByName, getRRuleString, notify, recurrence, selectedEvent, shiftPastBirthdayToNext, studentId, canPublishToAll, publishToAll, userEmail]);

  const handleDeleteEvent = useCallback(async () => {
    if (!selectedEvent) return;
    try {
      await deleteDoc(doc(db, 'events', selectedEvent.id));
      setDeleteModalOpen(false);
      setEditModalOpen(false);
      setSelectedEvent(null);
      notify('success', 'Evento eliminato con successo.');
    } catch (err) {
      console.error('Errore eliminazione evento:', err);
      notify('error', "Errore durante l'eliminazione dell'evento.");
    }
  }, [notify, selectedEvent]);

  // === Filtri ===
  const filteredEvents = useMemo(() => {
    const q = (searchRef.current || '').toLowerCase();
    return events.filter((ev) => {
      if (q && !(ev.title || '').toLowerCase().includes(q)) return false;
      if (categoryFilter !== 'all' && ev.category !== categoryFilter) return false;
      if (dateFrom && moment(ev.end).isBefore(moment(dateFrom).startOf('day'))) return false;
      if (dateTo && moment(ev.start).isAfter(moment(dateTo).endOf('day'))) return false;
      return true;
    });
  }, [events, categoryFilter, dateFrom, dateTo]);

  // === Nav ===
  const navigateToDashboard = () => { navigate('/Dashboard'); setIsOpenSidebar(false); };
  const navigateToLibrary = () => { navigate('/Library'); setIsOpenSidebar(false); };

  // === Export ICS (singolo) ===
  const downloadIcs = useCallback((ev) => {
    try {
      const dt = (d) => moment(d).utc().format('YYYYMMDD[T]HHmmss[Z]');
      const uid = `${ev.id || Math.random().toString(36).slice(2)}@diariodibordo`;
      const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//DiarioDiBordo//Calendar//IT',
        'CALSCALE:GREGORIAN',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${dt(new Date())}`,
        `DTSTART:${ev.allDay ? moment(ev.start).utc().format('YYYYMMDD') : dt(ev.start)}`,
        `DTEND:${ev.allDay ? moment(ev.end || ev.start).utc().format('YYYYMMDD') : dt(ev.end || ev.start)}`,
        `SUMMARY:${(ev.title || '').replace(/\n/g, ' ')}`,
        `CATEGORIES:${ev.category || ''}`,
        'END:VEVENT',
        'END:VCALENDAR',
      ];
      const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(ev.title || 'evento').replace(/\s+/g, '_')}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      console.error('ICS export error:', err);
      notify('error', 'Impossibile esportare il file .ics');
    }
  }, [notify]);

  // === Export ICS (multiplo) ===
  const downloadIcsBulk = useCallback(() => {
    const selected = getUpcomingEvents().filter((ev) => selectedForExport.has(ev.id));
    if (!selected.length) { notify('warning', 'Seleziona almeno un evento.'); return; }
    try {
      const dt = (d) => moment(d).utc().format('YYYYMMDD[T]HHmmss[Z]');
      const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//DiarioDiBordo//Calendar//IT',
        'CALSCALE:GREGORIAN',
      ];
      selected.forEach((ev) => {
        const uid = `${ev.id || Math.random().toString(36).slice(2)}@diariodibordo`;
        lines.push(
          'BEGIN:VEVENT',
          `UID:${uid}`,
          `DTSTAMP:${dt(new Date())}`,
          `DTSTART:${ev.allDay ? moment(ev.start).utc().format('YYYYMMDD') : dt(ev.start)}`,
          `DTEND:${ev.allDay ? moment(ev.end || ev.start).utc().format('YYYYMMDD') : dt(ev.end || ev.start)}`,
          `SUMMARY:${(ev.title || '').replace(/\n/g, ' ')}`,
          `CATEGORIES:${ev.category || ''}`,
          'END:VEVENT'
        );
      });
      lines.push('END:VCALENDAR');
      const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Eventi_Selezionati_${moment().format('YYYYMMDD_HHmm')}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      notify('success', 'File .ics multiplo generato.');
    } catch (err) {
      console.error('ICS bulk export error:', err);
      notify('error', 'Impossibile esportare il file .ics multiplo');
    }
  }, [getUpcomingEvents, notify, selectedForExport]);

  // === DnD / Resize ===
  const moveEvent = useCallback(async ({ event, start, end, isAllDay }) => {
    try {
      await updateDoc(doc(db, 'events', event.id), {
        start,
        end,
        allDay: typeof isAllDay === 'boolean' ? isAllDay : event.allDay,
      });
      notify('success', 'Evento spostato.');
    } catch (err) {
      console.error('moveEvent error:', err);
      notify('error', 'Errore durante lo spostamento.');
    }
  }, [notify]);

  const resizeEvent = useCallback(async ({ event, start, end }) => {
    try {
      await updateDoc(doc(db, 'events', event.id), { start, end });
      notify('success', 'Durata evento aggiornata.');
    } catch (err) {
      console.error('resizeEvent error:', err);
      notify('error', 'Errore durante il resize.');
    }
  }, [notify]);

  // Toggle "Seleziona tutti" per export ICS
  const handleToggleSelectAll = useCallback((checked) => {
    setSelectAllChecked(checked);
    if (checked) {
      const allIds = getUpcomingEvents().map((ev) => ev.id);
      setSelectedForExport(new Set(allIds));
    } else {
      setSelectedForExport(new Set());
    }
  }, [getUpcomingEvents]);

  // === RENDER ===
  const EventContent = useMemo(
    () => makeEventContent(openPreview, closePreview, previewForEventRef),
    [openPreview, closePreview]
  );

  const renderStudentOption = (props, option) => (
    <li {...props} key={option.id}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar src={option.photoURL || ''} alt={option.fullName} sx={{ width: 28, height: 28 }} />
        <span>{option.fullName}</span>
      </Box>
    </li>
  );

  const onStudentPicked = (studentObj) => {
    if (!studentObj) return;
    const birth = parseBirthDate(studentObj.birthDate);
    const occ = computeBirthdayOccurrence(birth, shiftPastBirthdayToNext);
    setEventTitle(studentObj.fullName || '');
    setAllDay(true);
    setRecurrence('yearly');
    if (occ?.start && occ?.end) {
      setEventStart(occ.start);
      setEventEnd(occ.end);
    }
    if (studentObj.photoURL) setCoverUrl(studentObj.photoURL);
    setStudentId(studentObj.id);
  };

  return (
    <div className="calendar-container">
      <Sidebar isOpen={isOpenSidebar} toggleSidebar={() => setIsOpenSidebar(!isOpenSidebar)} />

      <div className="calendar-content">
        <AppBar position="fixed" color="primary" className="app-bar">
          <Toolbar>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setIsOpenSidebar(!isOpenSidebar)} className="menu-button">
              <FaBars />
            </IconButton>

            <Typography variant="h6" className="app-title">Calendario</Typography>
            <div style={{ flexGrow: 1 }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
              <Typography variant="subtitle1" className="current-date">{currentDateFormatted}</Typography>

              <Tooltip title="Dashboard">
                <IconButton edge="end" color="inherit" aria-label="dashboard" onClick={navigateToDashboard} className="calendar-icon-button">
                  <Badge
                    badgeContent={upcomingEventsCount}
                    color="error"
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                  >
                    <Badge
                      badgeContent={birthdayNotifications}
                      color="warning"
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', color: '#000' } }}
                    >
                      <FaHome />
                    </Badge>
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Libreria">
                <IconButton edge="end" color="inherit" aria-label="libreria" onClick={navigateToLibrary} className="library-icon-button">
                  <FaBook />
                </IconButton>
              </Tooltip>
            </div>
          </Toolbar>
        </AppBar>
        <Toolbar />

        <Container maxWidth="lg" className="calendar-container-content">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                label="Cerca appuntamenti"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" margin="normal">
                <InputLabel id="category-filter-label">Categoria</InputLabel>
                <Select
                  labelId="category-filter-label"
                  label="Categoria"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">Tutte</MenuItem>
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Da"
                  value={dateFrom}
                  onChange={(v) => setDateFrom(v)}
                  slotProps={{ textField: { fullWidth: true, size: 'small', margin: 'normal' } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="A"
                  value={dateTo}
                  onChange={(v) => setDateTo(v)}
                  slotProps={{ textField: { fullWidth: true, size: 'small', margin: 'normal' } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              {showHint && (
                <Alert
                  severity="info"
                  variant="outlined"
                  className="hint-alert"
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          localStorage.setItem('calendarHintDismissed', '1');
                          setShowHint(false);
                        }}
                      >
                        Non mostrare più
                      </Button>
                      <IconButton aria-label="chiudi suggerimento" onClick={() => setShowHint(false)} size="small">
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                >
                  <AlertTitle>Suggerimento rapido</AlertTitle>
                  Clicca su un giorno del calendario per <strong>aggiungere un appuntamento</strong>. Puoi anche usare i pulsanti <em>Oggi / Mese / Settimana / Giorno</em> qui sotto.
                </Alert>
              )}
              <Paper className="paper" style={{ padding: 12 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {CATEGORIES.map((c) => (
                    <Box key={c.name} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      <span style={{ display: 'inline-block', width: 14, height: 14, background: c.color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }} />
                      <Typography variant="caption" sx={{ color: '#333' }}>{c.name}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper className="paper">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" onClick={() => { setDate(new Date()); }}>Oggi</Button>
                  <Button variant="outlined" size="small" onClick={() => { setView(Views.MONTH); localStorage.setItem('calendarView', Views.MONTH); }}>Mese</Button>
                  <Button variant="outlined" size="small" onClick={() => { setView(Views.WEEK); localStorage.setItem('calendarView', Views.WEEK); }}>Settimana</Button>
                  <Button variant="outlined" size="small" onClick={() => { setView(Views.DAY); localStorage.setItem('calendarView', Views.DAY); }}>Giorno</Button>
                  <Typography variant="body2" sx={{ ml: 'auto', opacity: 0.8 }}>
                    Suggerimento: trascina gli eventi per spostarli o allunga i bordi per cambiarne la durata
                  </Typography>
                </Box>

                <DnDCalendar
                  localizer={localizer}
                  events={filteredEvents}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 620 }}
                  selectable
                  popup
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={handleSelectEvent}
                  eventPropGetter={eventStyleGetter}
                  messages={messages}
                  formats={formats}
                  view={view}
                  onView={(v) => { setView(v); localStorage.setItem('calendarView', v); }}
                  date={date}
                  onNavigate={(d) => setDate(d)}
                  resizable
                  onEventDrop={moveEvent}
                  onEventResize={resizeEvent}
                  components={{ event: EventContent }}
                />
              </Paper>
            </Grid>

            {/* Prossimi appuntamenti + export multiplo */}
            <Grid item xs={12}>
              <Paper className="paper">
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h6" gutterBottom sx={{ flex: 1 }}>
                    Prossimi Appuntamenti
                  </Typography>

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selectAllChecked}
                        onChange={(e) => handleToggleSelectAll(e.target.checked)}
                      />
                    }
                    label="Seleziona tutti"
                  />

                  <Button
                    size="small"
                    variant="contained"
                    onClick={downloadIcsBulk}
                    disabled={selectedForExport.size === 0}
                  >
                    Esporta .ics (selezionati)
                  </Button>

                  {preloadingPhotos && (
                    <Box display="inline-flex" alignItems="center" gap={1} ml={1}>
                      <CircularProgress size={18} />
                      <Typography variant="caption">Caricamento foto studenti…</Typography>
                    </Box>
                  )}
                </Box>

                <List>
                  {getUpcomingEvents().map((ev, idx) => {
                    const isBdayNoPhoto = ev.category === 'Compleanno' && !ev.coverUrl;
                    const uploadingThis = uploadingName === ev.title;
                    const checked = selectedForExport.has(ev.id);
                    return (
                      <ListItem
                        key={`${ev.id}-${idx}`}
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={(e) => {
                                    setSelectedForExport((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(ev.id);
                                      else next.delete(ev.id);
                                      const total = getUpcomingEvents().length;
                                      setSelectAllChecked(next.size > 0 && next.size === total);
                                      return next;
                                    });
                                  }}
                                />
                              }
                              label="Seleziona"
                            />
                            {isBdayNoPhoto ? (
                              <label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) uploadStudentPhoto(ev.title, file);
                                  }}
                                />
                                <Button size="small" variant="contained" component="span" disabled={uploadingThis}>
                                  {uploadingThis ? 'Carico…' : 'Carica foto'}
                                </Button>
                              </label>
                            ) : (
                              <Button size="small" variant="outlined" onClick={() => downloadIcs(ev)}>
                                .ics
                              </Button>
                            )}
                            <Button size="small" variant="outlined" onClick={() => handleSelectEvent(ev)}>
                              Apri
                            </Button>
                          </Box>
                        }
                      >
                        {(ev.coverUrl || isBdayNoPhoto) && (
                          <ListItemAvatar>
                            {ev.coverUrl ? (
                              <Avatar variant="rounded" src={ev.coverUrl} alt={ev.title} sx={{ width: 48, height: 48, mr: 1 }} />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 48, height: 48, mr: 1, bgcolor: '#E0E0E0', color: '#555' }}>
                                {ev.title?.trim()?.charAt(0)?.toUpperCase() || '?'}
                              </Avatar>
                            )}
                          </ListItemAvatar>
                        )}
                        <ListItemText
                          primary={ev.title}
                          secondary={`${moment(ev.start).format('dddd, D MMMM YYYY')}${ev.allDay ? '' : `, ${moment(ev.start).format('HH:mm')}`}`}
                        />
                      </ListItem>
                    );
                  })}
                  {getUpcomingEvents().length === 0 && <Typography>Nessun appuntamento imminente.</Typography>}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Popover anteprima cover */}
        <Popover
          open={preview.open}
          onClose={closePreview}
          anchorEl={preview.anchorEl}          disableEnforceFocus
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{ sx: { p: 0.5, borderRadius: 1, boxShadow: 8, pointerEvents: 'none', zIndex: 1600 } }}
        >
          <img src={preview.url} alt="cover" style={{ display: 'block', maxWidth: 280, maxHeight: 220, borderRadius: 6 }} />
        </Popover>

        {/* Modal Aggiungi */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} aria-labelledby="add-event-modal-title">
          <Box className="modal-box">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" id="add-event-modal-title">Aggiungi Appuntamento</Typography>
                    <IconButton onClick={() => setModalOpen(false)} aria-label="chiudi"><CloseIcon /></IconButton>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField label="Titolo" value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} fullWidth variant="outlined" required />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DateTimePicker label="Inizio" value={eventStart} onChange={(v) => setEventStart(v)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker label="Fine" value={eventEnd} onChange={(v) => setEventEnd(v)} disabled={allDay} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="recurrence-label">Ricorrenza</InputLabel>
                    <Select labelId="recurrence-label" label="Ricorrenza" value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
                      <MenuItem value="none">Nessuna</MenuItem>
                      <MenuItem value="daily">Giornaliera</MenuItem>
                      <MenuItem value="weekly">Settimanale</MenuItem>
                      <MenuItem value="monthly">Mensile</MenuItem>
                      <MenuItem value="yearly">Annuale</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="category-label">Categoria</InputLabel>
                    <Select
                      labelId="category-label"
                      label="Categoria"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map((cat) => (<MenuItem key={cat.name} value={cat.name}>{cat.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>

                {canPublishToAll && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={publishToAll}
                          onChange={(e) => { setPendingPublishValue(e.target.checked); setPublishConfirmOpen(true); }}
                        />
                      }
                      label={publishToAll ? 'Pubblicazione: TUTTI GLI UTENTI' : 'Pubblicazione: SOLO IO'}
                    />
                  </Grid>
                )}

                {category === 'Compleanno' && (
                  <>
                    <Grid item xs={12}>
                      <Autocomplete
                        loading={loadingStudents}
                        options={students}
                        getOptionLabel={(o) => o.fullName || ''}
                        onChange={(_, value) => onStudentPicked(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Studente (per importare nome, data e foto)"
                            placeholder="Digita il nome..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingStudents ? <CircularProgress size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={renderStudentOption}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={shiftPastBirthdayToNext}
                            onChange={(e) => setShiftPastBirthdayToNext(e.target.checked)}
                          />
                        }
                        label="Sposta al prossimo anniversario se quello di quest’anno è già passato"
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <FormControlLabel control={<Checkbox checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />} label="Tutto il giorno" />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField label="Copertina (URL - opzionale)" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} fullWidth variant="outlined" placeholder="https://..." />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button variant="outlined" fullWidth component="label" disabled={uploadingCover}>
                    {uploadingCover ? 'Carico…' : 'Carica immagine'}
                    <input hidden type="file" accept="image/*" onChange={async (e) => { const f = e.target.files?.[0]; if (f) await uploadCoverFile(f); }} />
                  </Button>
                </Grid>
                {coverUrl && (
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} alignItems="center">
                      <Avatar variant="rounded" src={coverUrl} alt="Anteprima cover" sx={{ width: 72, height: 72 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>Anteprima cover</Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField label="ID Studente (opzionale)" value={studentId} onChange={(e) => setStudentId(e.target.value)} fullWidth variant="outlined" />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" sx={{ gap: 2 }}>
                    <Button variant="contained" color="primary" onClick={handleAddEvent}>Aggiungi</Button>
                    <Button variant="outlined" onClick={() => setModalOpen(false)}>Annulla</Button>
                  </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>
        </Modal>

        {/* Modal Modifica */}
        <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} aria-labelledby="edit-event-modal-title">
          <Box className="modal-box">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" id="edit-event-modal-title">Modifica Appuntamento</Typography>
                    <IconButton onClick={() => setEditModalOpen(false)} aria-label="chiudi"><CloseIcon /></IconButton>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField label="Titolo" value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} fullWidth variant="outlined" required />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DateTimePicker label="Inizio" value={eventStart} onChange={(v) => setEventStart(v)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker label="Fine" value={eventEnd} onChange={(v) => setEventEnd(v)} disabled={allDay} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="recurrence-edit-label">Ricorrenza</InputLabel>
                    <Select labelId="recurrence-edit-label" label="Ricorrenza" value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
                      <MenuItem value="none">Nessuna</MenuItem>
                      <MenuItem value="daily">Giornaliera</MenuItem>
                      <MenuItem value="weekly">Settimanale</MenuItem>
                      <MenuItem value="monthly">Mensile</MenuItem>
                      <MenuItem value="yearly">Annuale</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="category-edit-label">Categoria</InputLabel>
                    <Select
                      labelId="category-edit-label"
                      label="Categoria"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map((cat) => (<MenuItem key={cat.name} value={cat.name}>{cat.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>

                {canPublishToAll && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={publishToAll}
                          onChange={(e) => { setPendingPublishValue(e.target.checked); setPublishConfirmOpen(true); }}
                        />
                      }
                      label={publishToAll ? 'Pubblicazione: TUTTI GLI UTENTI' : 'Pubblicazione: SOLO IO'}
                    />
                  </Grid>
                )}

                {category === 'Compleanno' && (
                  <>
                    <Grid item xs={12}>
                      <Autocomplete
                        loading={loadingStudents}
                        options={students}
                        getOptionLabel={(o) => o.fullName || ''}
                        onChange={(_, value) => onStudentPicked(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Studente (importa nome, data e foto)"
                            placeholder="Digita il nome..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingStudents ? <CircularProgress size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={renderStudentOption}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={shiftPastBirthdayToNext}
                            onChange={(e) => setShiftPastBirthdayToNext(e.target.checked)}
                          />
                        }
                        label="Sposta al prossimo anniversario se quello di quest’anno è già passato"
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <FormControlLabel control={<Checkbox checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />} label="Tutto il giorno" />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField label="Copertina (URL - opzionale)" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} fullWidth variant="outlined" placeholder="https://..." />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button variant="outlined" fullWidth component="label" disabled={uploadingCover}>
                    {uploadingCover ? 'Carico…' : 'Carica immagine'}
                    <input hidden type="file" accept="image/*" onChange={async (e) => { const f = e.target.files?.[0]; if (f) await uploadCoverFile(f); }} />
                  </Button>
                </Grid>
                {coverUrl && (
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} alignItems="center">
                      <Avatar variant="rounded" src={coverUrl} alt="Anteprima cover" sx={{ width: 72, height: 72 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>Anteprima cover</Typography>
                    </Box>
                  </Grid>
                )}

                {category === 'Compleanno' && !coverUrl && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>Nessuna foto collegata alla scheda studente.</Typography>
                      <label>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const url = await uploadStudentPhoto(eventTitle.trim(), file);
                              if (url) {
                                setCoverUrl(url);
                                notify('success', 'Foto caricata: anteprima aggiornata.');
                              }
                            }
                          }}
                        />
                        <Button size="small" variant="contained" component="span" disabled={uploadingName === eventTitle.trim()}>
                          {uploadingName === eventTitle.trim() ? 'Carico…' : 'Carica foto studente'}
                        </Button>
                      </label>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField label="ID Studente (opzionale)" value={studentId} onChange={(e) => setStudentId(e.target.value)} fullWidth variant="outlined" />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between">
                    <Button variant="contained" color="secondary" onClick={() => setDeleteModalOpen(true)}>Elimina</Button>
                    <Box display="flex" sx={{ gap: 2 }}>
                      <Button variant="contained" color="primary" onClick={handleEditEvent}>Salva</Button>
                      <Button variant="outlined" onClick={() => setEditModalOpen(false)}>Annulla</Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>
        </Modal>

        {/* Conferma Eliminazione */}
        <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} aria-labelledby="delete-event-modal-title">
          <Box className="modal-box">
            <Typography variant="h5" id="delete-event-modal-title">Elimina Appuntamento</Typography>
            <Typography>Sei sicuro di voler eliminare questo appuntamento?</Typography>
            <Box display="flex" justifyContent="flex-end" marginTop={2} sx={{ gap: 2 }}>
              <Button variant="contained" color="secondary" onClick={handleDeleteEvent}>Elimina</Button>
              <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>Annulla</Button>
            </Box>
          </Box>
        </Modal>

        <footer className="calendar-footer">
          <div className="social-buttons">
            <a href="https://www.tuosito.com" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Sito Internet"><FaGlobe /></a>
            <a href="https://www.facebook.com/tuoprofilo" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Facebook"><FaFacebookF /></a>
            <a href="https://wa.me/tuonumber" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="WhatsApp"><FaWhatsapp /></a>
            <a href="https://www.instagram.com/tuoprofilo" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Instagram"><FaInstagram /></a>
            <a href="https://www.pinterest.com/tuoprofilo" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Pinterest"><FaPinterestP /></a>
          </div>
          <p className="footer-text">&copy; {new Date().getFullYear()} Parrocchia del S.S. Redentore - Monserrato</p>
        </footer>

        <Dialog open={publishConfirmOpen} onClose={() => { setPublishConfirmOpen(false); setPendingPublishValue(null); }}>
          <DialogTitle>Conferma modalità di pubblicazione</DialogTitle>
          <DialogContent>
            <Typography>
              Stai impostando la visibilità su: <strong>{pendingPublishValue ? 'TUTTI GLI UTENTI' : 'SOLO IO'}</strong>.
            </Typography>
            <Typography variant="caption" display="block" style={{ marginTop: 12 }}>
              {pendingPublishValue
                ? 'Tutti gli utenti vedranno una notifica alla prossima apertura della web app.'
                : 'L’evento resterà visibile solo a te.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setPublishConfirmOpen(false); setPendingPublishValue(null); }}>Annulla</Button>
            <Button variant="contained" onClick={() => { if (pendingPublishValue !== null) setPublishToAll(!!pendingPublishValue); setPublishConfirmOpen(false); setPendingPublishValue(null); }}>Conferma</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={3000} onClose={closeSnack} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={closeSnack} severity={snack.type} variant="filled" sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default CalendarPage;
